import React from "react";

function formatCurrency(value) {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default function ProductDetailInfo({
  product,
  quantity,
  setQuantity,
  onAddToCart,
}) {
  const hasDiscount =
    Number(product?.original_price || 0) > Number(product?.price || 0);
  const maxQty = Math.max(1, Number(product?.stock || 1));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-medium text-orange-700">
          {product?.category_name || "Produk IoT"}
        </span>
        {product?.sku ? (
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
            SKU: {product.sku}
          </span>
        ) : null}
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">
          {product?.name}
        </h1>
        {product?.subtitle ? (
          <p className="mt-2 text-base text-gray-600">{product.subtitle}</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
        <span>⭐ {Number(product?.rating || 0).toFixed(1)}</span>
        <span>•</span>
        <span>Terjual {Number(product?.sold_count || 0)}</span>
        <span>•</span>
        <span className={Number(product?.stock) > 0 ? "text-emerald-600" : "text-red-600"}>
          {Number(product?.stock) > 0 ? `Stok ${product?.stock}` : "Stok habis"}
        </span>
      </div>

      <div>
        {hasDiscount && (
          <p className="text-base text-gray-400 line-through">
            {formatCurrency(product?.original_price)}
          </p>
        )}
        <p className="text-3xl font-extrabold text-orange-600">
          {formatCurrency(product?.price)}
        </p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <p className="text-sm font-medium text-gray-900">Deskripsi singkat</p>
        <p className="mt-2 text-sm leading-7 text-gray-600">
          {product?.short_description || product?.description || "Belum ada deskripsi singkat."}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Jumlah
          </label>
          <div className="flex items-center overflow-hidden rounded-xl border border-gray-300 bg-white">
            <button
              type="button"
              onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
              className="px-4 py-3 text-lg text-gray-700 hover:bg-gray-50"
            >
              −
            </button>
            <input
              type="number"
              min={1}
              max={maxQty}
              value={quantity}
              onChange={(event) => {
                const value = Number(event.target.value || 1);
                if (Number.isNaN(value)) return;
                setQuantity(Math.min(maxQty, Math.max(1, value)));
              }}
              className="w-20 border-x border-gray-300 px-3 py-3 text-center text-sm outline-none"
            />
            <button
              type="button"
              onClick={() => setQuantity((prev) => Math.min(maxQty, prev + 1))}
              className="px-4 py-3 text-lg text-gray-700 hover:bg-gray-50"
            >
              +
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onAddToCart}
          disabled={Number(product?.stock) <= 0}
          className="inline-flex min-w-[220px] items-center justify-center rounded-xl bg-orange-600 px-5 py-3.5 text-sm font-semibold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:bg-gray-300"
        >
          {Number(product?.stock) > 0 ? "Tambah ke Keranjang" : "Stok Habis"}
        </button>
      </div>

      {Array.isArray(product?.attributes) && product.attributes.length > 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Spesifikasi
          </h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {product.attributes.map((attribute, index) => (
              <div
                key={`${attribute.attribute_name}-${index}`}
                className="rounded-xl border border-gray-100 bg-gray-50 p-3"
              >
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {attribute.attribute_name || "Atribut"}
                </p>
                <p className="mt-1 text-sm font-semibold text-gray-900">
                  {attribute.attribute_value || "-"}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
